import { useState } from 'react'

import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { css, styled } from 'styled-components'
import { useBoolean } from 'usehooks-ts'

import { lineClamp } from '@b-stock/fe-core/bstock-next/components'
import {
  useIsContentTruncated,
  Button,
  FormattedMessage,
} from '@b-stock/fe-core/bstock-react'

import { MarkdownRenderer } from '@/components/MarkdownRenderer'

const Body = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const Content = styled.div<{
  $isExpanded: boolean
  $isTruncated: boolean
}>`
  ${lineClamp(12)}
  position: relative;
  ${(o) =>
    o.$isExpanded &&
    css`
      overflow: initial;
      && {
        -webkit-line-clamp: initial;
      }
      padding-bottom: 0.02rem;
    `}
  ${(o) =>
    o.$isTruncated &&
    `
    &::after {
      position: absolute;
      content: ' ';
      height: 5rem;
      width: 100%;
      bottom: 0;
      left: 0;
      background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0) 0%,
        #ffffff 100%
      );
      pointer-events: none;
    }
  `}
`

const Icon = styled(FontAwesomeIcon)`
  font-size: 0.8125rem;
  margin-left: 0.25rem;
`

const ns = 'Listing.listingDetails.lotDescription'

export const LotDescription = ({
  description,
  linkClickedHandler,
}: {
  description?: string
  linkClickedHandler?: (href: string) => void
}) => {
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
    null
  )

  const isContentTruncated = useIsContentTruncated(contentElement)
  const { value: isExpanded, toggle: toggleExpanded } = useBoolean(false)

  const showToggle = isContentTruncated || isExpanded

  if (!description) {
    return null
  }

  return (
    <Body>
      <Content
        ref={setContentElement}
        data-testid="lot-description"
        $isExpanded={isExpanded}
        $isTruncated={isContentTruncated}
      >
        <MarkdownRenderer linkClickedHandler={linkClickedHandler}>
          {description}
        </MarkdownRenderer>
      </Content>
      {showToggle ? (
        <Button
          appearance="plainTertiary"
          type="button"
          onClick={toggleExpanded}
        >
          {isExpanded ? (
            <>
              <FormattedMessage id={`${ns}.hide`} />
              <Icon icon={faChevronUp} />
            </>
          ) : (
            <>
              <FormattedMessage id={`${ns}.seeMore`} />
              <Icon icon={faChevronDown} />
            </>
          )}
        </Button>
      ) : null}
    </Body>
  )
}
